// src/pages/system/SingleCustomCollection/details/index.jsx
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useNotifications } from '@/hooks/useNotifications'
import LoadingIndicator from '@/widgets/LoadingIndicator'
import ItemFields from './ItemFields'
import ItemActions from './ItemActions'
import DependentCollectionList from './DependentCollectionList'
import useFormData from '../common/useFormData'

export default function SingleCustomCollectionDetails() {
	const { t } = useTranslation()
	const { collectionId, itemId } = useParams()
	const navigate = useNavigate()
	const {
		collectionDetails,
		itemData,
		lookupOptions,
		loadingCollection,
		loadingItem,
		loadError,
		deleteError,
		deleting,
		setLoadError,
		setDeleteError,
		deleteItem,
	} = useFormData({ collectionId, itemId })

	const aggregatedError = deleteError || loadError

	const { showDeleteConfirmation, showDeleteSuccess } = useNotifications({
		error: aggregatedError,
		clearError: () => {
			if (loadError) setLoadError(null)
			if (deleteError) setDeleteError(null)
		},
	})

	const loading = loadingCollection || loadingItem

	const handleDelete = async () => {
		if (!itemData?._id) return

		const confirmed = await showDeleteConfirmation(1, t('singleCustomCollection.details.deleteDialog.confirmText'))
		if (!confirmed) return
		const { success } = await deleteItem(itemData._id)
		if (!success) return

		showDeleteSuccess(1, t('singleCustomCollection.list.messages.deletedSuccess'))
		navigate(`/single-custom-collection/${collectionId}`, { state: { message: t('singleCustomCollection.list.messages.deletedSuccess') } })
	}

	if (loading) { return <LoadingIndicator message={t('singleCustomCollection.details.loading')} /> }

	if (!collectionDetails || !itemData) {
		return (
			<div className="rounded-lg bg-white p-6 shadow-sm">
				<p className="text-sm text-gray-600">{t('singleCustomCollection.details.unavailable')}</p>
			</div>
		)
	}

	const item = { ...itemData }

	return (
		<>
			<ul role="list" className="grid grid-cols-1 gap-6">
				{/* Details card */}
				<li className="col-span-1 rounded-lg bg-white shadow-sm">
					{/* Header */}
					<div className="flex w-full items-center justify-between space-x-6 p-6 py-4">
						<div className="flex-1 truncate">
							<div className="flex items-center space-x-3">
								<h2 className="truncate text-lg text-gray-900 font-semibold">
									{item.name || item.title || t('singleCustomCollection.details.itemTitleFallback', { name: collectionDetails.name })}
								</h2>
							</div>
							<p className="mt-1 truncate text-sm text-gray-500">{t('singleCustomCollection.details.labels.id')}: {item._id}</p>
							<p className="mt-1 truncate text-sm text-gray-500">{t('singleCustomCollection.details.labels.collection')}: {collectionDetails.name}</p>
						</div>
					</div>

					{/* Item data */}
					<ItemFields item={item} collectionDetails={collectionDetails} lookupOptions={lookupOptions} />

					{/* Actions */}
					<ItemActions
						item={item}
						collectionId={collectionId}
						onEdit={() => navigate(`/single-custom-collection/${collectionId}/edit/${item._id}`)}
						onAddNew={() => navigate(`/single-custom-collection/${collectionId}/add`)}
						onDelete={handleDelete}
						onBack={() => navigate(`/single-custom-collection/${collectionId}`)}
						deleting={deleting}
					/>
				</li>
			</ul>

			<DependentCollectionList parentCollectionDetails={collectionDetails} item={item} />
		</>
	)
}
